/* ==========================================================================
   FOLDARO — interaction layer
   Vanilla, dependency-free. Every listener is bound directly to its own
   element (never delegated), because Horizon delegates `on:` events on
   `document` with { capture: true } and would otherwise pre-empt us.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  function onceVisible(el, cb, threshold) {
    if (!('IntersectionObserver' in window)) { cb(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { cb(); io.disconnect(); }
      });
    }, { threshold: threshold == null ? 0.35 : threshold });
    io.observe(el);
  }

  /* ------------------------------------------------------- 1. reveal ---- */
  function initReveal() {
    var els = $$('.fd-reveal');
    if (!els.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    els.forEach(function (el) {
      // Stagger siblings that opt in via data-fd-stagger on the parent.
      var parent = el.parentElement;
      if (parent && parent.hasAttribute('data-fd-stagger')) {
        var sibs = $$('.fd-reveal', parent);
        var i = sibs.indexOf(el);
        if (i > -1) el.style.setProperty('--fd-delay', (i * 80) + 'ms');
      }
      io.observe(el);
    });
  }


  /* -------------------------------------------------- 1b. word cascade -- */
  function initWords() {
    $$('[data-fd-words]').forEach(function (el) {
      if (el.dataset.fdWordsDone) return;
      el.dataset.fdWordsDone = '1';

      var text = el.textContent.replace(/\s+/g, ' ').trim();
      if (!text) return;
      var parts = text.split(' ');
      el.textContent = '';
      parts.forEach(function (w, i) {
        var span = document.createElement('span');
        span.className = 'fd-word';
        span.textContent = w;
        span.style.setProperty('--fd-wd', (i * 65) + 'ms');
        el.appendChild(span);
        if (i < parts.length - 1) el.appendChild(document.createTextNode(' '));
      });

      var spans = $$('.fd-word', el);
      function play() { spans.forEach(function (s) { s.classList.add('is-in'); }); }
      if (REDUCED) { play(); return; }
      // Hero copy is above the fold: play immediately rather than on scroll.
      if (el.hasAttribute('data-fd-words-now')) {
        requestAnimationFrame(function () { requestAnimationFrame(play); });
      } else {
        onceVisible(el, play, 0.3);
      }
    });
  }

  /* ---------------------------------------------------- 2. count-up ----- */
  function countUp(el) {
    var to = parseFloat(el.getAttribute('data-fd-count'));
    if (isNaN(to)) return;
    var from = parseFloat(el.getAttribute('data-fd-from') || '0');
    var dur = parseInt(el.getAttribute('data-fd-dur') || '2000', 10);
    var dec = parseInt(el.getAttribute('data-fd-dec') || '0', 10);

    if (REDUCED) { el.textContent = to.toFixed(dec); return; }

    var start = null;
    function frame(ts) {
      if (start === null) start = ts;
      var p = clamp((ts - start) / dur, 0, 1);
      // easeOutExpo
      var e = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      el.textContent = (from + (to - from) * e).toFixed(dec);
      if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function initCounters() {
    $$('[data-fd-count]').forEach(function (el) {
      onceVisible(el, function () { countUp(el); }, 0.5);
    });
    // Thermometer fill rides along with its readout.
    $$('.fd-thermo').forEach(function (t) {
      var fill = $('.fd-thermo__fill', t);
      if (!fill) return;
      onceVisible(t, function () {
        fill.style.setProperty('--fd-fill', (fill.getAttribute('data-fd-fill') || '88') + '%');
      }, 0.4);
    });
  }

  /* ------------------------------------------- 3. before/after slider --- */
  function initBeforeAfter() {
    $$('.fd-ba').forEach(function (ba) {
      var dragging = false;

      function setSplit(clientX) {
        var r = ba.getBoundingClientRect();
        var pct = clamp(((clientX - r.left) / r.width) * 100, 0, 100);
        ba.style.setProperty('--fd-split', pct + '%');
      }
      function down(e) {
        dragging = true;
        ba.classList.add('is-touched');
        setSplit(e.clientX);
        if (ba.setPointerCapture && e.pointerId != null) {
          try { ba.setPointerCapture(e.pointerId); } catch (_) {}
        }
      }
      function move(e) { if (dragging) setSplit(e.clientX); }
      function up() { dragging = false; }

      ba.addEventListener('pointerdown', down);
      ba.addEventListener('pointermove', move);
      ba.addEventListener('pointerup', up);
      ba.addEventListener('pointercancel', up);

      // Keyboard access
      ba.setAttribute('tabindex', '0');
      ba.setAttribute('role', 'slider');
      ba.setAttribute('aria-label', 'Compare parked in direct sun versus shaded by Foldaro');
      ba.setAttribute('aria-valuemin', '0');
      ba.setAttribute('aria-valuemax', '100');
      ba.setAttribute('aria-valuenow', '50');
      ba.addEventListener('keydown', function (e) {
        var cur = parseFloat((ba.style.getPropertyValue('--fd-split') || '50%')) || 50;
        var step = e.shiftKey ? 10 : 4;
        var next = cur;
        if (e.key === 'ArrowLeft')  next = clamp(cur - step, 0, 100);
        if (e.key === 'ArrowRight') next = clamp(cur + step, 0, 100);
        if (e.key === 'Home') next = 0;
        if (e.key === 'End')  next = 100;
        if (next !== cur) {
          e.preventDefault();
          ba.style.setProperty('--fd-split', next + '%');
          ba.setAttribute('aria-valuenow', String(Math.round(next)));
          ba.classList.add('is-touched');
        }
      });

      // Invite interaction: a gentle sweep the first time it is seen.
      if (!REDUCED) {
        onceVisible(ba, function () {
          var t0 = null;
          function nudge(ts) {
            if (t0 === null) t0 = ts;
            var p = clamp((ts - t0) / 1500, 0, 1);
            if (ba.classList.contains('is-touched')) return;
            var e = 0.5 - Math.cos(p * Math.PI * 2) * 0.5;
            ba.style.setProperty('--fd-split', (50 - e * 22) + '%');
            if (p < 1) requestAnimationFrame(nudge);
          }
          requestAnimationFrame(nudge);
        }, 0.4);
      }
    });
  }

  /* ------------------------------------------------ 4. how-it-works ----- */
  function initDemo() {
    $$('[data-fd-demo]').forEach(function (wrap) {
      var demo  = $('.fd-demo', wrap);
      var steps = $$('.fd-step', wrap);
      var replay = $('.fd-demo__replay', wrap);
      if (!demo) return;

      var timers = [];
      function clearTimers() { timers.forEach(clearTimeout); timers = []; }

      function run() {
        clearTimers();
        demo.classList.remove('is-shaded');
        steps.forEach(function (s) { s.classList.remove('is-active'); });
        if (steps[0]) steps[0].classList.add('is-active');

        if (REDUCED) {
          demo.classList.add('is-shaded');
          steps.forEach(function (s) { s.classList.add('is-active'); });
          return;
        }
        timers.push(setTimeout(function () {
          demo.classList.add('is-shaded');
          if (steps[0]) steps[0].classList.remove('is-active');
          if (steps[1]) steps[1].classList.add('is-active');
        }, 900));
        timers.push(setTimeout(function () {
          if (steps[1]) steps[1].classList.remove('is-active');
          if (steps[2]) steps[2].classList.add('is-active');
        }, 2700));
      }

      onceVisible(wrap, run, 0.35);
      if (replay) replay.addEventListener('click', run);
    });
  }

  /* -------------------------------------------------- 5. sticky ATC ----- */
  function initSticky() {
    var bar = $('[data-fd-sticky]');
    if (!bar) return;
    var trigger = $('[data-fd-sticky-after]') || $('.fd-hero');
    if (!trigger) return;

    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        // Show once the trigger has scrolled past the top of the viewport.
        bar.classList.toggle('is-shown', !e.isIntersecting && e.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    io.observe(trigger);
  }

  /* ------------------------------------------------------ 6. fitment ---- */
  /* Popular models per make. "Other" is always appended so no visitor is
     ever blocked — the supplier custom-cuts to whatever is submitted. */
  var CARS = {
    'Acura': ['ILX','MDX','RDX','TLX','Integra','ZDX'],
    'Alfa Romeo': ['Giulia','Stelvio','Tonale'],
    'Audi': ['A3','A4','A5','A6','A7','A8','Q3','Q4 e-tron','Q5','Q7','Q8','e-tron GT','TT'],
    'BMW': ['1 Series','2 Series','3 Series','4 Series','5 Series','7 Series','X1','X3','X4','X5','X6','X7','i4','iX','Z4'],
    'BYD': ['Atto 3','Dolphin','Han','Seal','Song','Tang'],
    'Buick': ['Enclave','Encore','Envision','Envista'],
    'Cadillac': ['CT4','CT5','Escalade','Lyriq','XT4','XT5','XT6'],
    'Chevrolet': ['Blazer','Bolt','Camaro','Colorado','Equinox','Malibu','Silverado','Suburban','Tahoe','Trailblazer','Traverse','Trax'],
    'Chrysler': ['300','Pacifica','Voyager'],
    'Citroen': ['C3','C4','C5 Aircross','Berlingo'],
    'Dodge': ['Challenger','Charger','Durango','Hornet'],
    'Fiat': ['500','500X','Panda','Tipo'],
    'Ford': ['Bronco','Bronco Sport','Edge','Escape','Expedition','Explorer','F-150','Focus','Fusion','Kuga','Maverick','Mustang','Mustang Mach-E','Puma','Ranger','Transit'],
    'GMC': ['Acadia','Canyon','Sierra','Terrain','Yukon'],
    'Genesis': ['G70','G80','G90','GV70','GV80'],
    'Honda': ['Accord','Civic','CR-V','Fit','HR-V','Insight','Jazz','Odyssey','Passport','Pilot','Prologue','Ridgeline'],
    'Hyundai': ['Accent','Elantra','i10','i20','i30','Ioniq 5','Ioniq 6','Kona','Palisade','Santa Fe','Sonata','Tucson','Venue'],
    'Infiniti': ['Q50','QX50','QX55','QX60','QX80'],
    'Jaguar': ['E-Pace','F-Pace','I-Pace','XE','XF'],
    'Jeep': ['Cherokee','Compass','Gladiator','Grand Cherokee','Renegade','Wagoneer','Wrangler'],
    'Kia': ['Carnival','Ceed','EV6','EV9','Forte','K5','Niro','Optima','Picanto','Rio','Seltos','Sorento','Soul','Sportage','Telluride'],
    'Land Rover': ['Defender','Discovery','Discovery Sport','Range Rover','Range Rover Evoque','Range Rover Sport','Range Rover Velar'],
    'Lexus': ['ES','GX','IS','LS','NX','RX','RZ','TX','UX'],
    'Lincoln': ['Aviator','Corsair','Nautilus','Navigator'],
    'Mazda': ['CX-30','CX-5','CX-50','CX-9','CX-90','Mazda2','Mazda3','Mazda6','MX-5 Miata'],
    'Mercedes-Benz': ['A-Class','C-Class','E-Class','S-Class','CLA','GLA','GLB','GLC','GLE','GLS','EQB','EQE','EQS','Sprinter'],
    'MG': ['MG4','MG5','HS','ZS'],
    'MINI': ['Clubman','Countryman','Hardtop','Cooper'],
    'Mitsubishi': ['ASX','Eclipse Cross','Outlander','Pajero','Triton'],
    'Nissan': ['Altima','Ariya','Frontier','Juke','Kicks','Leaf','Maxima','Murano','Navara','Pathfinder','Qashqai','Rogue','Sentra','Versa','X-Trail'],
    'Opel': ['Astra','Corsa','Crossland','Grandland','Mokka'],
    'Peugeot': ['208','2008','308','3008','5008'],
    'Polestar': ['Polestar 2','Polestar 3','Polestar 4'],
    'Porsche': ['911','Cayenne','Macan','Panamera','Taycan'],
    'RAM': ['1500','2500','3500','ProMaster'],
    'Renault': ['Captur','Clio','Kadjar','Megane','Scenic'],
    'Rivian': ['R1S','R1T'],
    'SEAT': ['Arona','Ateca','Ibiza','Leon'],
    'Skoda': ['Fabia','Karoq','Kodiaq','Octavia','Superb','Enyaq'],
    'Subaru': ['Ascent','BRZ','Crosstrek','Forester','Impreza','Legacy','Outback','Solterra','WRX'],
    'Suzuki': ['Baleno','Jimny','Swift','Vitara','S-Cross'],
    'Tesla': ['Model 3','Model 3 Highland','Model S','Model X','Model Y','Model Y Juniper','Cybertruck'],
    'Toyota': ['4Runner','Avalon','bZ4X','C-HR','Camry','Corolla','Corolla Cross','Highlander','Hilux','Land Cruiser','Prius','RAV4','Sequoia','Sienna','Tacoma','Tundra','Yaris'],
    'Vauxhall': ['Astra','Corsa','Grandland','Mokka'],
    'Volkswagen': ['Arteon','Atlas','Golf','ID.3','ID.4','ID.5','Jetta','Passat','Polo','T-Cross','T-Roc','Taos','Tiguan','Touareg'],
    'Volvo': ['C40','EX30','EX90','S60','S90','V60','XC40','XC60','XC90'],
    'Other make': ['Other model']
  };

  function initFitment() {
    $$('[data-fd-fitment]').forEach(function (root) {
      var makeSel  = $('[data-fd-make]', root);
      var modelSel = $('[data-fd-model]', root);
      var yearSel  = $('[data-fd-year]', root);
      var note     = $('[data-fd-fitnote]', root);
      if (!makeSel || !modelSel || !yearSel) return;

      // Populate makes
      Object.keys(CARS).forEach(function (make) {
        var o = document.createElement('option');
        o.value = make; o.textContent = make;
        makeSel.appendChild(o);
      });

      // Populate years (current year + 1 back to 1995)
      var now = new Date().getFullYear();
      for (var y = now + 1; y >= 1995; y--) {
        var oy = document.createElement('option');
        oy.value = String(y); oy.textContent = String(y);
        yearSel.appendChild(oy);
      }

      modelSel.disabled = true;

      makeSel.addEventListener('change', function () {
        modelSel.innerHTML = '<option value="">Model</option>';
        var models = CARS[makeSel.value];
        if (!models) { modelSel.disabled = true; syncNote(root); return; }
        models.concat(models.indexOf('Other model') > -1 ? [] : ['Other model']).forEach(function (m) {
          var o = document.createElement('option');
          o.value = m; o.textContent = m;
          modelSel.appendChild(o);
        });
        modelSel.disabled = false;
        modelSel.classList.remove('is-error');
        makeSel.classList.remove('is-error');
        syncNote(root);
        if (typeof remember === 'function') remember();
      });

      modelSel.addEventListener('change', function () { modelSel.classList.remove('is-error'); syncNote(root); remember(); });
      yearSel.addEventListener('change',  function () { yearSel.classList.remove('is-error');  syncNote(root); remember(); });

      function remember() {
        try {
          localStorage.setItem('fd_car', JSON.stringify({
            make: makeSel.value, model: modelSel.value, year: yearSel.value
          }));
        } catch (_) {}
      }

      // Restore a previously chosen vehicle
      (function restore() {
        var raw;
        try { raw = localStorage.getItem('fd_car'); } catch (_) { return; }
        if (!raw) return;
        var v;
        try { v = JSON.parse(raw); } catch (_) { return; }
        if (!v || !v.make || !CARS[v.make]) return;

        makeSel.value = v.make;
        makeSel.dispatchEvent(new Event('change'));
        if (v.model) {
          var hasModel = Array.prototype.some.call(modelSel.options, function (o) { return o.value === v.model; });
          if (hasModel) modelSel.value = v.model;
        }
        if (v.year) yearSel.value = v.year;
        syncNote(root);
      })();

      function syncNote(r) {
        var v = readFitment(r);
        if (!note) return;
        if (v) {
          note.classList.add('is-confirmed');
          note.querySelector('[data-fd-fittext]').textContent =
            'Your Foldaro will be custom-cut for the ' + v + '.';
        } else {
          note.classList.remove('is-confirmed');
          note.querySelector('[data-fd-fittext]').textContent =
            'Every shade is custom-cut to your exact windshield.';
        }
      }
    });
  }

  /* True when the shopper has opened the free-text box and written something. */
  function altFitmentOpen(root) {
    var panel = $('[data-fd-fitalt-panel]', root);
    return !!(panel && !panel.hidden);
  }
  function altFitmentText(root) {
    var input = $('[data-fd-fitalt-text]', root);
    return input ? input.value.trim() : '';
  }

  function readFitment(root) {
    /* The typed car takes precedence: if they opened the box and wrote a car, that
       is the most specific thing they told us, even if a dropdown is also set. */
    if (altFitmentOpen(root)) {
      var typed = altFitmentText(root);
      if (typed) return typed;
    }
    var makeSel  = $('[data-fd-make]', root);
    var modelSel = $('[data-fd-model]', root);
    var yearSel  = $('[data-fd-year]', root);
    if (!makeSel || !modelSel || !yearSel) return '';
    if (!makeSel.value || !modelSel.value || !yearSel.value) return '';
    return yearSel.value + ' ' + makeSel.value + ' ' + modelSel.value;
  }

  function validateFitment(root) {
    /* If they typed their car, that satisfies the requirement by itself — holding
       them to the dropdowns as well is the exact friction this box exists to
       remove. Four characters is enough to reject an accidental keypress without
       trying to guess what a valid car string looks like. */
    if (altFitmentOpen(root)) {
      var input = $('[data-fd-fitalt-text]', root);
      var typed = altFitmentText(root);
      if (typed.length >= 4) {
        if (input) input.classList.remove('is-error');
        return true;
      }
      if (input) input.classList.add('is-error');
      return false;
    }
    var ok = true;
    ['[data-fd-make]', '[data-fd-model]', '[data-fd-year]'].forEach(function (s) {
      var el = $(s, root);
      if (el && !el.value) { el.classList.add('is-error'); ok = false; }
    });
    return ok;
  }

  /* ------------------------------------------- fitment: the escape hatch */
  function initFitmentAlt() {
    $$('[data-fd-fitalt]').forEach(function (alt) {
      var toggle = $('[data-fd-fitalt-toggle]', alt);
      var panel  = $('[data-fd-fitalt-panel]', alt);
      var input  = $('[data-fd-fitalt-text]', alt);
      var root   = alt.closest('[data-fd-fitment]');
      if (!toggle || !panel) return;

      function open(next, focus) {
        panel.hidden = !next;
        toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
        alt.classList.toggle('is-open', next);
        if (next && focus && input) input.focus();
        if (!next && input) { input.value = ''; input.classList.remove('is-error'); }
        /* Opening the box clears any error the dropdowns were showing, and vice
           versa, so the shopper is never told off about a field they abandoned. */
        if (root) {
          $$('[data-fd-make],[data-fd-model],[data-fd-year]', root).forEach(function (el) {
            el.classList.remove('is-error');
          });
          var err = root.querySelector('[data-fd-error]');
          if (err) err.classList.remove('is-shown');
        }
      }

      toggle.addEventListener('click', function () {
        open(panel.hidden, true);
      });

      if (input) {
        input.addEventListener('input', function () {
          if (input.value.trim().length >= 4) input.classList.remove('is-error');
        });
      }

      /* Picking "Other" is the moment the list has failed them — open the box for
         them rather than making them find the link. */
      if (root) {
        $$('[data-fd-make],[data-fd-model]', root).forEach(function (sel) {
          sel.addEventListener('change', function () {
            if (/^other/i.test(sel.value || '')) open(true, false);
          });
        });
      }
    });
  }

  /* ------------------------------------------------ 7. add to cart ------ */
  function initCart() {
    $$('[data-fd-atc]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var scope = document.querySelector(btn.getAttribute('data-fd-scope') || '[data-fd-buybox]');
        var variantId = btn.getAttribute('data-fd-variant');
        var required = btn.getAttribute('data-fd-fitment-required') === 'true';
        var errBox = scope ? $('[data-fd-error]', scope) : null;
        var fitRoot = scope ? $('[data-fd-fitment]', scope) : null;

        // Fitment gate
        if (required && fitRoot && !validateFitment(fitRoot)) {
          if (errBox) errBox.classList.add('is-shown');
          fitRoot.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'center' });
          return;
        }
        if (errBox) errBox.classList.remove('is-shown');

        var qtyInput = scope ? $('[data-fd-qty]', scope) : null;
        var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

        var props = {};
        var fit = fitRoot ? readFitment(fitRoot) : '';
        if (fit) props['Car model & year'] = fit;

        var original = btn.innerHTML;
        btn.setAttribute('aria-disabled', 'true');
        btn.innerHTML = 'Adding…';

        var body = { id: variantId, quantity: qty };
        if (Object.keys(props).length) body.properties = props;

        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root + 'cart/add.js'
              : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || j.message || 'Could not add to cart'); });
          return r.json();
        })
        .then(function () {
          // Single-product store: send straight to checkout rather than a
          // cart drawer, which is where this funnel historically leaks.
          var dest = btn.getAttribute('data-fd-after') || 'checkout';
          window.location.href = dest === 'cart' ? '/cart' : '/checkout';
        })
        .catch(function (err) {
          btn.removeAttribute('aria-disabled');
          btn.innerHTML = original;
          if (errBox) {
            var t = $('[data-fd-errtext]', errBox);
            if (t) t.textContent = err.message || 'Something went wrong. Please try again.';
            errBox.classList.add('is-shown');
          }
        });
      });
    });

    // Quantity steppers
    $$('[data-fd-qty-wrap]').forEach(function (wrap) {
      var input = $('[data-fd-qty]', wrap);
      if (!input) return;
      $$('[data-fd-step]', wrap).forEach(function (b) {
        b.addEventListener('click', function () {
          var d = parseInt(b.getAttribute('data-fd-step'), 10) || 0;
          input.value = String(Math.max(1, (parseInt(input.value, 10) || 1) + d));
        });
      });
    });
  }


  /* --------------------------------------------- 9. announcement bar ---- */
  function initAnnounce() {
    // Rotating messages
    $$('[data-fd-rotate]').forEach(function (rot) {
      var msgs = $$('.fd-ann__msg', rot);
      if (msgs.length < 2) return;
      var every = (parseFloat(rot.getAttribute('data-fd-rotate')) || 5) * 1000;
      var i = 0;
      setInterval(function () {
        msgs[i].classList.remove('is-on');
        i = (i + 1) % msgs.length;
        msgs[i].classList.add('is-on');
      }, every);
    });

    // Countdown to a REAL deadline. If the deadline has passed we hide the
    // timer rather than looping it — a timer that restarts per visitor is a
    // fake-urgency dark pattern.
    $$('[data-fd-countdown]').forEach(function (bar) {
      var raw = bar.getAttribute('data-fd-countdown');
      var end = new Date(raw.replace(' ', 'T'));
      if (isNaN(end.getTime())) return;
      var box = $('[data-fd-timer]', bar);
      var val = $('[data-fd-timer-value]', bar);
      if (!box || !val) return;

      function tick() {
        var diff = end.getTime() - Date.now();
        if (diff <= 0) { box.hidden = true; clearInterval(id); return; }
        var d = Math.floor(diff / 86400000);
        var h = Math.floor(diff / 3600000) % 24;
        var m = Math.floor(diff / 60000) % 60;
        var s = Math.floor(diff / 1000) % 60;
        function pad(n) { return n < 10 ? '0' + n : String(n); }
        val.textContent = (d > 0 ? d + 'd ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s);
        box.hidden = false;
      }
      tick();
      var id = setInterval(tick, 1000);
    });
  }

  /* -------------------------------------------------------- 8. boot ----- */

  /* ------------------------------------------------------- header drawer */
  /* Bound directly to the button, not delegated — see the note at the top of
     this file about Horizon capturing document-level events. */
  function initHeader() {
    var root = $('[data-fd-header]');
    if (!root) return;
    var btn = $('[data-fd-hdr-toggle]', root);
    var drawer = $('[data-fd-hdr-drawer]', root);
    var scrim = $('[data-fd-hdr-scrim]', root);
    if (!btn || !drawer) return;

    function setOpen(open) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      drawer.hidden = !open;
      if (scrim) scrim.hidden = !open;
      /* Lock the page behind the sheet so the drawer does not scroll the hero. */
      document.documentElement.style.overflow = open ? 'hidden' : '';
    }

    btn.addEventListener('click', function () {
      setOpen(btn.getAttribute('aria-expanded') !== 'true');
    });
    if (scrim) scrim.addEventListener('click', function () { setOpen(false); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && btn.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
        btn.focus();
      }
    });

    /* Any in-drawer navigation should close it, including same-page anchors
       where no page load happens to close it for us. */
    $$('a', drawer).forEach(function (a) {
      a.addEventListener('click', function () { setOpen(false); });
    });

    /* Crossing out of the mobile breakpoint with the sheet open would leave the
       page locked and the sheet stranded under the desktop nav. */
    if (window.matchMedia) {
      var mq = window.matchMedia('(min-width: 861px)');
      var onChange = function (e) { if (e.matches) setOpen(false); };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }
  }


  /* --------------------------------------------------------------- reels rail */
  function initReels() {
    $$('[data-fd-reels]').forEach(function (shell) {
      var track = $('[data-fd-reels-track]', shell);
      if (!track) return;
      var prev = $('[data-fd-reels-prev]', shell);
      var next = $('[data-fd-reels-next]', shell);

      function page() {
        var card = track.querySelector('.fd-reel');
        /* Scroll by one card plus the real gap, so a card never lands half-visible. */
        if (!card) return track.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap || '12') || 12;
        return card.getBoundingClientRect().width + gap;
      }
      function sync() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -page(), behavior: REDUCED ? 'auto' : 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: page(), behavior: REDUCED ? 'auto' : 'smooth' }); });
      /* Dots double as the position readout and as jump targets. */
      var dots = $$('[data-fd-reels-dot]', shell);
      function syncDots() {
        if (!dots.length) return;
        var card = track.querySelector('.fd-reel');
        if (!card) return;
        var w = card.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
        var i = w ? Math.round(track.scrollLeft / w) : 0;
        dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
      }
      dots.forEach(function (d, n) {
        d.addEventListener('click', function () {
          var card = track.querySelector('.fd-reel');
          if (!card) return;
          var w = card.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
          track.scrollTo({ left: w * n, behavior: REDUCED ? 'auto' : 'smooth' });
        });
      });

      track.addEventListener('scroll', function () { sync(); syncDots(); }, { passive: true });
      window.addEventListener('resize', function () { sync(); syncDots(); });
      sync(); syncDots();

      /* Only one clip audible/playing at a time — a rail of six all running is both
         a bandwidth problem and unusable. */
      $$('video', track).forEach(function (v) {
        v.addEventListener('play', function () {
          $$('video', track).forEach(function (o) { if (o !== v) o.pause(); });
        });
      });
    });
  }


  /* ------------------------------------------------------- buy box gallery */
  function initGallery() {
    $$('[data-fd-gallery]').forEach(function (root) {
      var stage = $('[data-fd-stage]', root);
      var thumbs = $$('[data-fd-thumb]', root);
      if (!stage || !thumbs.length) return;
      thumbs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var src = btn.getAttribute('data-fd-thumb');
          if (!src || stage.getAttribute('src') === src) return;
          /* Preload before swapping so the stage never flashes empty. */
          var pre = new Image();
          pre.onload = function () { stage.src = src; };
          pre.src = src;
          thumbs.forEach(function (t) { t.classList.toggle('is-active', t === btn); });
        });
      });
    });
  }


  /* ---------------------------------------------------------- press button */
  /* The product's whole gesture, performed on the page: press once, the shade
     rises and the cabin cools. Press again to put it back. */
  function initPress() {
    $$('[data-fd-press]').forEach(function (root) {
      var btn = $('[data-fd-press-btn]', root);
      var deg = $('[data-fd-press-deg]', root);
      if (!btn) return;

      var hot = parseInt(deg && deg.textContent, 10);
      var cool = parseInt(root.getAttribute('data-fd-cool') || '32', 10);
      if (isNaN(hot)) hot = 71;
      var on = false, raf = null;

      /* Count the number rather than swapping it — the fall is the point, and it
         is timed to the shade so the two read as one event. */
      function countTo(target, ms) {
        if (!deg) return;
        if (raf) cancelAnimationFrame(raf);
        var from = parseInt(deg.textContent, 10);
        if (isNaN(from)) from = hot;
        if (REDUCED) { deg.textContent = target; return; }
        var t0 = performance.now();
        (function step(now) {
          /* Clamp both ends: the first rAF callback can report a timestamp
             marginally before t0, which makes p negative and counts the
             temperature the wrong way for a frame. */
          var p = Math.max(0, Math.min(1, (now - t0) / ms));
          /* easeOutCubic, so most of the drop is visible early */
          var e = 1 - Math.pow(1 - p, 3);
          deg.textContent = Math.round(from + (target - from) * e);
          if (p < 1) raf = requestAnimationFrame(step);
        })(t0);
      }

      function set(next) {
        on = next;
        root.classList.toggle('is-on', on);
        root.classList.add('is-used');
        countTo(on ? cool : hot, on ? 1150 : 700);

        /* If there is a video behind this, restart it on the press so the
           gesture and the footage line up. */
        var vid = root.parentElement && root.parentElement.querySelector('video');
        if (vid && on) {
          try { vid.currentTime = 0; var pr = vid.play(); if (pr && pr.catch) pr.catch(function () {}); } catch (e) {}
        }
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      }

      btn.setAttribute('aria-pressed', 'false');
      btn.addEventListener('click', function () { set(!on); });
    });
  }


  /* --------------------------------------------------------- reviews rail */
  function initReviews() {
    $$('[data-fd-revs]').forEach(function (shell) {
      var track = $('[data-fd-revs-track]', shell);
      var dots = $$('[data-fd-revs-dot]', shell);
      if (!track || !dots.length) return;

      function step() {
        var card = track.querySelector('.fd-rev');
        if (!card) return 0;
        return card.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
      }
      function sync() {
        var w = step();
        var i = w ? Math.round(track.scrollLeft / w) : 0;
        dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
      }
      dots.forEach(function (d, n) {
        d.addEventListener('click', function () {
          var w = step();
          track.scrollTo({ left: w * n, behavior: REDUCED ? 'auto' : 'smooth' });
        });
      });
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  function boot() {
    initWords();
    initReveal();
    initCounters();
    initBeforeAfter();
    initDemo();
    initHeader();
    initReels();
    initGallery();
    initPress();
    initReviews();
    initSticky();
    initFitment();
    initFitmentAlt();
    initCart();
    initAnnounce();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
